import React, { MutableRefObject } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { QuestionStatus, QuestionType, QuizQuestion } from '@atlassian/learning-components';
import shuffle from 'lodash.shuffle';

import { User } from 'src/types';
import { getImageAttrs, getInspectModeData } from 'src/utils/contentful';
import { getFeedbackMessageOptions } from 'src/pages/LessonPage/children/utils/optionsUtils';
import { ContentfulSmLesson, ContentfulSmQuestion } from 'src/graphql/queries/GetSmLesson';
import { sendTrackEvent } from 'src/utils/analytics';
import { QuizStatus } from 'src/pages/LessonPage/children/Quiz';
import { ContentfulSmCourse } from 'src/graphql/queries/GetSmCourse';
import {
  completeQuestionForQuiz,
  getUncompletedQuestionsForQuiz,
  LearningProgressStatus,
  ProgressForLessons,
} from 'src/services/http/progress';
import { isStandalone } from 'src/pages/LessonPage/utils/isStandalone';
import { getSlug } from 'src/utils/slug';

export interface QuizInteractionAnalytics {
  lessonId?: string;
  startTimeMs: number;
  notStarted: boolean;
  isDirty: boolean;
}

const QuestionTypeConverter: Record<ContentfulSmQuestion['type'], QuestionType> = {
  multiple: QuestionType.MULTI_SELECT,
  single: QuestionType.SINGLE_SELECT,
  matching: QuestionType.MATCHING,
};

export const getLastUncompletedQuestion = (questions: ContentfulSmQuestion[], uncompletedQuestions: string[]): number =>
  questions?.map((q) => !uncompletedQuestions.includes(q.sys.id)).lastIndexOf(true);

export const sortQuestions = (lesson: ContentfulSmLesson, uncompletedQuestions: string[]): ContentfulSmQuestion[] => {
  if (!lesson || !lesson.quiz) {
    return [];
  }

  return [...lesson.quiz.questionsCollection.items].sort((q) => (uncompletedQuestions.includes(q.sys.id) ? 1 : -1));
};

export const getMatchingQuestions = (
  questions: ContentfulSmQuestion[],
  preview: boolean,
  lesson: ContentfulSmLesson,
  user?: User | null | undefined,
): QuizQuestion[] =>
  questions.map((question) => {
    const leftAnswers = question.answersCollection.items.map((answer) => ({
      inspectMode: getInspectModeData(answer, ['title']),
      title: answer.title,
      matchingAnswer: { title: answer.matchingAnswer?.title || '' },
      correct: false,
      isLeftMatchingAnswer: true,
    }));

    const rightAnswers = question.answersCollection.items.map((answer) => ({
      inspectMode: getInspectModeData(answer.matchingAnswer, ['title']),
      title: answer.matchingAnswer?.title || '',
      matchingAnswer: { title: answer.title },
      correct: false,
      isLeftMatchingAnswer: false,
    }));

    return {
      inspectMode: getInspectModeData(question, ['title']),
      title: <span>{documentToReactComponents(question.title.json)}</span>,
      imageUrl: question.image ? getImageAttrs(question.image)?.src : '',
      feedbackMessage: <>{documentToReactComponents(question.feedbackMessage?.json, getFeedbackMessageOptions)}</>,
      answers: question.answersCollection.items.map((answer) => ({
        ...answer,
        inspectMode: getInspectModeData(answer, ['title', 'feedback']),
      })),
      type: QuestionTypeConverter[question.type],
      leftAnswers: shuffle(leftAnswers),
      rightAnswers: shuffle(rightAnswers),
      onCompletion: (): void => {
        if (!preview && user) {
          completeQuestionForQuiz({
            atlassianId: user.account_id,
            lessonId: lesson.sys.id,
            quizId: lesson.quiz.sys.id,
            questionId: question.sys.id,
          });
        }
      },
    };
  });

export const handleAnswerSubmit = (
  type: QuestionType,
  status: QuestionStatus,
  courseId: string | undefined,
  lessonId: string | undefined,
  quizStep: number,
  user: User | null | undefined,
): void => {
  if (status !== QuestionStatus.CORRECT) {
    sendTrackEvent(
      'lessonScreen',
      'quizQuestion',
      'answeredWrong',
      {
        courseSlug: courseId,
        lessonSlug: lessonId,
        questionIndex: quizStep,
        questionType: type,
        answerStatus: status,
        isStandaloneLesson: isStandalone(),
      },
      user?.account_id,
    );
  }
};

export const getUncompletedQuestionsAndStepAsync = async (
  user: User | null | undefined,
  lesson: ContentfulSmLesson,
): Promise<{ questions: ContentfulSmQuestion[]; step: number; isClean?: boolean }> => {
  if (!user) {
    return {
      questions: lesson.quiz.questionsCollection.items,
      step: -1,
      isClean: true,
    };
  }

  const uncompletedQuestions = await getUncompletedQuestionsForQuiz({
    atlassianId: user.account_id,
    lessonId: lesson.sys.id,
    quizId: lesson.quiz.sys.id,
    allQuestionIds: lesson.quiz?.questionsCollection.items.map((q) => q.sys.id) ?? [],
  });

  if (!uncompletedQuestions?.uncompletedQuestionIds?.length) {
    return {
      questions: [],
      step: 0,
    };
  }

  const questions = sortQuestions(lesson, uncompletedQuestions.uncompletedQuestionIds);
  const step = getLastUncompletedQuestion(questions, uncompletedQuestions.uncompletedQuestionIds);

  return {
    questions,
    step,
    isClean: uncompletedQuestions.uncompletedQuestionIds.length === questions.length,
  };
};

export const sendCompletionAnalytics = (
  course: ContentfulSmCourse | undefined,
  lesson: ContentfulSmLesson,
  progress: ProgressForLessons | undefined,
  user: User | null | undefined,
  quizInteractionAnalytics: QuizInteractionAnalytics | null | undefined,
): void => {
  if (!user || !progress) {
    return;
  }

  const courseSlug = getSlug(course);
  const lessonSlug = getSlug(lesson);

  const quizCompletionDurationMs =
    quizInteractionAnalytics && new Date().getTime() - quizInteractionAnalytics.startTimeMs;

  sendTrackEvent(
    'lessonScreen',
    'quiz',
    'completed',
    {
      courseSlug,
      lessonSlug,
      interactionDurationMs: quizInteractionAnalytics?.notStarted ? quizCompletionDurationMs : 0,
    },
    user.account_id,
  );

  if (!course) {
    return;
  }

  const areLessonsCompleted = course.lessonsCollection.items.every((l) => {
    const lessonProgress = progress.lessons.find((p) => p.lessonId === l.sys.id);

    if (lessonProgress?.lessonId === lesson.sys.id) {
      return true;
    }

    return lessonProgress?.status === LearningProgressStatus.COMPLETED;
  });

  if (areLessonsCompleted) {
    sendTrackEvent(
      'learningApp',
      'course',
      'completed',
      {
        courseSlug,
      },
      user.account_id,
    );
  }
};

export const handleUnload = ({
  lesson,
  user,
  quizInteractionAnalytics,
  quizAbandonedAnalyticsAttributes,
}: {
  lesson: ContentfulSmLesson;
  user: User | null | undefined;
  quizInteractionAnalytics: MutableRefObject<QuizInteractionAnalytics | null>;
  quizAbandonedAnalyticsAttributes: MutableRefObject<null | Record<string, string | number | undefined>>;
}): void => {
  const interactionDurationMs =
    quizInteractionAnalytics.current &&
    !!quizInteractionAnalytics.current.startTimeMs &&
    new Date().getTime() - quizInteractionAnalytics.current.startTimeMs;

  quizInteractionAnalytics.current = {
    lessonId: lesson.sys.id,
    startTimeMs: 0,
    notStarted: false,
    isDirty: false,
  };

  if (quizAbandonedAnalyticsAttributes.current && interactionDurationMs) {
    sendTrackEvent(
      'lessonScreen',
      'quiz',
      'abandoned',
      {
        ...quizAbandonedAnalyticsAttributes.current,
        interactionDurationMs,
      },
      user?.account_id,
    );
    quizAbandonedAnalyticsAttributes.current = null;
  }
};

export const handleInteraction = (
  quizInteractionAnalytics: MutableRefObject<QuizInteractionAnalytics | null>,
  quizStatus: QuizStatus,
  lesson: ContentfulSmLesson,
): void => {
  if (
    quizInteractionAnalytics.current &&
    !quizInteractionAnalytics.current?.isDirty &&
    quizStatus !== QuizStatus.COMPLETED
  ) {
    quizInteractionAnalytics.current = {
      isDirty: true,
      lessonId: lesson.sys.id,
      startTimeMs: new Date().getTime(),
      notStarted: quizInteractionAnalytics.current?.notStarted,
    };
  }
};
